import React from 'react';
import { SDA_TABLE_CLASSES, SDA_TABLE_STYLE, SDA_HEADING_CELL_CLASSES, SdaColGroup } from './test-tile';
import { TOOLBAR_HEIGHT, SDA_COLUMN_HEADINGS_HEIGHT } from '../constants';

/**
 * SDA's per-tile "Clause/Requirement/Result/Observations/Recommendations" column heading row,
 * hoisted to render ONCE above the results list instead of repeating on every tile
 * (test-tile.tsx used to render it inline via TextTile titles — raytd/web QA 108.03, item 1).
 *
 * Lives outside the virtualizer (VirtualizedNestedList/Virtuoso), pinned directly below the
 * toolbar (raytd/web QA 108.03 item 17, QA 108.04 item 1: QA asked for these to stick while
 * scrolling, reversing the prior round's deliberate non-sticky choice). Its fixed height
 * (`SDA_COLUMN_HEADINGS_HEIGHT`) is added to the virtualizer's own sticky group-header offset
 * (VirtualisedItemList.tsx) so the two stacked sticky rows sit flush with no gap between them
 * (QA 108.03 item 18). The published/read-only view (published/SdaComplianceTable.tsx) already
 * renders its own column headings correctly and is untouched by this component.
 */
// "Clause" abbreviates to "Cl." — its column was shrunk (raytd/web QA 108.03 refail, item 9)
// and the full word doesn't fit comfortably at that width.
// Exported so the print pipeline's own once-per-page column heading (sda-print-column-
// headings.tsx, raytd/web QA 108.02 item 2) uses the exact same label set instead of a
// hand-duplicated copy.
export const SDA_RESULTS_COLUMN_HEADINGS = ['Cl.', 'Requirement', 'Result', 'Observations', 'Recommendations'] as const;

const SdaResultsColumnHeadings: React.FC = () => (
    <table
        data-testid="sda-results-column-headings"
        // Table-family, sharing SDA_TABLE_CLASSES/SdaColGroup with the tile's own content table
        // (test-tile.tsx) and the print heading below, so all three stay pixel-aligned from one
        // source instead of duplicated width literals (raytd/web#153).
        className={`${SDA_TABLE_CLASSES} border-b border-zinc-200 bg-white`}
        style={{
            ...SDA_TABLE_STYLE,
            position: 'sticky',
            top: TOOLBAR_HEIGHT,
            height: SDA_COLUMN_HEADINGS_HEIGHT,
            zIndex: 41, // above the group headers (zIndex 40) so it never scrolls behind one
        }}
    >
        <SdaColGroup />
        <thead>
            <tr>
                {SDA_RESULTS_COLUMN_HEADINGS.map((heading) => (
                    <th key={heading} className={SDA_HEADING_CELL_CLASSES}>
                        {heading}
                    </th>
                ))}
            </tr>
        </thead>
    </table>
);

export default SdaResultsColumnHeadings;
